import { HbrDivider, HbrSpinner, HbrTextarea } from '@harbor/elements-react-standalone';
import React, { useMemo, useState } from 'react';
import { fetchItems } from '../../support/queries';
import { useFetchOpportunity, useFetchOpportunityActivityHistory } from '../../hooks/opportunity-hooks';
import { useParams } from 'react-router-dom';
import { OpportunityPhaseLabel } from '../../types/opportunity';
import StbModal from '../security-toolbox-ui/StbModal';


const SEWorkLogList: React.FC = () => {
  const { id: opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity(opportunityId);

  const [selectedActivity, setSelectedActivity] = useState<any | null>(null);
  const [activityOwner, setActivityOwner] = useState<{
    Name: string;
    Email: string;
  } | null>(null);

  const displayNotes = useMemo(() => {
    if (!opportunity?.SE_Notes__c) {
      return;
    }

    const entries = (opportunity?.SE_Notes__c ?? '').match(/^\d{4}-\d{2}-\d{2}.*$/gm) || []; // Current assumption that each entry in notes is on a new line
    return entries.map((entry) => {
      const [date, name, token, ...rest] = entry.split(' - ');
      if ([...Object.values(OpportunityPhaseLabel), 'Close'].includes(token)) {
        return {
          date,
          text: ` - ${token} - ${name}${rest && rest.length > 0 ? ` - ${rest.join(' - ')}` : ''}`
        };
      } else {
        return {
          date,
          text: ` - ${name} - ${token}${rest && rest.length > 0 ? ` - ${rest.join(' - ')}` : ''}`
        };
      }
    });
  }, [opportunity]);

  const { data: oppActivities, isLoading } = useFetchOpportunityActivityHistory(
    { opportunity: opportunity?.Id },
    { enabled: !!opportunity?.Id }
  );

  const showActivities = !isLoading && oppActivities && oppActivities.length > 0;

  const fetchActivityOwner = async (ownerId: string) => {
    const ownerData = await fetchItems(`sf/query`, {
      q: `SELECT Name, Email FROM User WHERE Id = '${ownerId}'`,
    });
    return ownerData[0];
  };

  const handleRowClick = async (activity: any) => {
    setSelectedActivity(activity);
    setActivityOwner(null);
    if (activity.OwnerId) {
      const ownerDetails = await fetchActivityOwner(activity.OwnerId);
      setActivityOwner(ownerDetails);
    }
  };

  const handleCloseModal = async () => {
    setSelectedActivity(null);
    setActivityOwner(null);
  };

  return (
    <>
    {showActivities && (
      <>
        <h3>Work History</h3>
          <div className='max-h-80 overflow-y-auto mb-8'>
            <div className='grid grid-cols-3'>
              <div className='text-sm font-medium'>Status</div>
              <div className='text-sm font-medium'>Date</div>
              <div className='text-sm font-medium'>Summary</div>
            </div>
            {(oppActivities ?? []).map((activity, idx) => (
              <div
                className='cursor-pointer grid-cols-3 grid hover:bg-gray-200'
                onClick={() => handleRowClick(activity)}
                key={idx}
              >
                <div className='text-sm my-1'>{activity.Status}</div>
                <div className='text-sm my-1'>{activity.ActivityDate}</div>
                <div className='text-sm my-1'>{activity.Subject}</div>
              </div>
            ))}
          </div>

          <HbrDivider className="mt-4" />
        </>
      )}

      {opportunity?.SE_Notes__c && (
        <>
          <h3>SE Notes</h3>
          <div className='max-h-80 overflow-y-auto'>
            <p className='text-sm'>
              {displayNotes.map((item, idx) => (
                <React.Fragment key={`SENote-${idx}`}>
                  <span className={'font-bold'}>{item.date}</span>
                  <span>{item.text}</span>
                  <br />
                </React.Fragment>
              ))}
            </p>
          </div>
        </>
      )}

      <StbModal
        role="information"
        title="Activity Details"
        open={!!selectedActivity}
        onClose={handleCloseModal}
      >
        <>
          <div className="grid grid-cols-[6rem_1fr] gap-x-4">
            <div className="text-sm font-semibold mb-2">Status:</div>
            <div className='text-sm text-gray-700 mb-2'>{selectedActivity?.Status ?? ''}</div>

            <div className='text-sm font-semibold mb-2'>Date:</div>
            <div className='text-sm text-gray-700 mb-2'>{selectedActivity?.ActivityDate ?? ''}</div>

            <div className='text-sm font-semibold mb-2'>Summary:</div>
            <div className='text-sm text-gray-700 mb-2'>{selectedActivity?.Subject ?? ''}</div>

            <div className='text-sm font-semibold mb-2'>Priority:</div>
            <div className='text-sm text-gray-700 mb-2'>{selectedActivity?.Priority ?? ''}</div>
          </div>

          <div className="text-sm font-semibold">Description:</div>
          {selectedActivity?.Description
            ? (
                <HbrTextarea
                  value={selectedActivity.Description}
                  disabled={true}
                  rows={3}
                />
              )
            : (
                <div className="text-sm text-gray-700 w-full min-h-24 my-2 italic border border-gray-300 p-2 rounded">
                  No description available
                </div>
              )
          }

          {activityOwner
            ? (
                <div className="grid grid-cols-[6rem_1fr] gap-x-4">
                  <div className="text-sm font-semibold mb-2">Owner:</div>
                  <div className='text-sm text-gray-700 mb-2'>{activityOwner?.Name ?? ''}</div>

                  <div className="text-sm font-semibold mb-2">Email:</div>
                  <div className='text-sm text-gray-700 mb-2'>{activityOwner?.Email ?? ''}</div>
                </div>
              )
            : (
                <div className="text-sm text-gray-700">
                  Loading owner details...
                </div>
              )
          }
        </>
      </StbModal>
    </>
  );
};

export default SEWorkLogList;
